import React, { forwardRef, useRef, useCallback } from 'react';
import styles from './MoneyInput.module.css';

export interface IMoneyInputProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange' | 'onBlur' | 'placeholder'
> {
  value: string;
  onChange: (value: string) => void;
  /** Optional blur listener (additive — #205). Absent = no listener side-effect; existing callers unaffected. */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  currency?: string;
  onCurrencyChange?: (currency: string) => void;
  disabled?: boolean;
  placeholder?: string;
  error?: string | null;
  inputRef?: React.Ref<HTMLInputElement>;
}

const DECIMAL_REGEX = /^\d*\.?\d*$/;

const MoneyInput = forwardRef<HTMLDivElement, IMoneyInputProps>(
  (
    {
      value,
      onChange,
      onBlur,
      currency = 'GBP',
      onCurrencyChange,
      disabled = false,
      placeholder = '0.00',
      error,
      className,
      inputRef,
      ...rest
    },
    rootRef,
  ) => {
    const fallbackRef = useRef<HTMLInputElement>(null);
    const ref = inputRef || fallbackRef;

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        if (newValue === '' || DECIMAL_REGEX.test(newValue)) {
          onChange(newValue);
        }
      },
      [onChange],
    );

    return (
      <div ref={rootRef} className={className} {...rest}>
        <div className={`${styles.inputContainer} ${error ? styles.error : ''}`}>
          <span className={styles.currencySymbol}>£</span>
          <input
            ref={ref}
            type="text"
            className={styles.amountInput}
            value={value}
            onChange={handleChange}
            onBlur={onBlur}
            disabled={disabled}
            placeholder={placeholder}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? 'money-input-error' : undefined}
          />
          {onCurrencyChange ? (
            <select
              className={styles.currencySelect}
              value={currency}
              onChange={(e) => onCurrencyChange(e.target.value)}
            >
              <option value="GBP">GBP</option>
            </select>
          ) : (
            <span className={styles.currencySelect}>{currency}</span>
          )}
        </div>
        {error && (
          <div id="money-input-error" role="alert" className={styles.errorMessage}>
            {error}
          </div>
        )}
      </div>
    );
  },
);

MoneyInput.displayName = 'MoneyInput';

export default MoneyInput;
